/**
 * 主题上下文
 * 管理应用的主题状态（亮色/暗色/跟随系统）
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// 主题模式
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// localStorage key
const THEME_STORAGE_KEY = 'admin_theme_mode';

// 创建上下文
const ThemeContext = createContext(null);

/**
 * 获取系统主题偏好
 */
const getSystemTheme = () => {
  if (typeof window === 'undefined') return THEME_MODES.LIGHT;
  try {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    return mediaQuery?.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT;
  } catch {
    return THEME_MODES.LIGHT;
  }
};

/**
 * 从 localStorage 读取主题设置
 */
const getStoredTheme = () => {
  if (typeof window === 'undefined') return THEME_MODES.LIGHT;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && Object.values(THEME_MODES).includes(stored)) {
      return stored;
    }
  } catch (e) {
    console.warn('Failed to read theme from localStorage:', e);
  }
  return THEME_MODES.LIGHT;
};

/**
 * 保存主题设置到 localStorage
 */
const saveTheme = (theme) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (e) {
    console.warn('Failed to save theme to localStorage:', e);
  }
};

/**
 * 应用主题到 DOM
 */
const applyThemeToDOM = (resolvedTheme) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolvedTheme);
  
  // 更新 meta theme-color
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      resolvedTheme === THEME_MODES.DARK ? '#141414' : '#ffffff'
    );
  }
};

/**
 * 主题 Provider
 */
export const ThemeProvider = ({ children }) => {
  // 用户选择的主题模式
  const [themeMode, setThemeMode] = useState(() => getStoredTheme());
  
  // 系统主题
  const [systemTheme, setSystemTheme] = useState(() => getSystemTheme());
  
  // 解析后的实际主题（light 或 dark）
  const resolvedTheme = useMemo(() => {
    if (themeMode === THEME_MODES.SYSTEM) {
      return systemTheme;
    }
    return themeMode;
  }, [themeMode, systemTheme]);
  
  // 是否为暗色主题
  const isDark = resolvedTheme === THEME_MODES.DARK;
  
  // 监听系统主题变化
  useEffect(() => {
    // 检查 matchMedia 是否可用
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        setSystemTheme(e.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT);
      };
      
      // 现代浏览器
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // 旧版浏览器
      if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    } catch {
      // 忽略错误
    }
  }, []);
  
  // 应用主题到 DOM
  useEffect(() => {
    applyThemeToDOM(resolvedTheme);
  }, [resolvedTheme]);
  
  // 设置主题
  const setTheme = useCallback((mode) => {
    if (Object.values(THEME_MODES).includes(mode)) {
      setThemeMode(mode);
      saveTheme(mode);
    }
  }, []);
  
  // 切换主题（在 light 和 dark 之间切换）
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === THEME_MODES.DARK 
      ? THEME_MODES.LIGHT 
      : THEME_MODES.DARK;
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);
  
  // 循环切换主题模式（light -> dark -> system -> light）
  const cycleTheme = useCallback(() => {
    const modes = [THEME_MODES.LIGHT, THEME_MODES.DARK, THEME_MODES.SYSTEM];
    const currentIndex = modes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setTheme(modes[nextIndex]);
  }, [themeMode, setTheme]);
  
  const value = useMemo(() => ({
    // 状态
    themeMode,        // 用户选择的模式：'light' | 'dark' | 'system'
    resolvedTheme,    // 实际应用的主题：'light' | 'dark'
    isDark,           // 是否为暗色主题
    systemTheme,      // 系统主题
    
    // 方法
    setTheme,         // 设置主题模式
    toggleTheme,      // 切换亮/暗
    cycleTheme,       // 循环切换模式
  }), [themeMode, resolvedTheme, isDark, systemTheme, setTheme, toggleTheme, cycleTheme]);
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * 使用主题的 Hook
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeContext;
